import { FormattedMessage, type IntlShape } from "react-intl";
import { Link, type ErrorComponentProps } from "@wayflyer/flyui";
import { IconClock32Line } from "@wayflyer/flyui-icons/32/line";
import { getLoginUrl } from "@wayflyer/auth-sdk";
import { handleContactTeam } from "shared-utils";

import { ERROR_MESSAGES } from "./messages";

export type ErrorVariant = "400" | "401" | "403" | "404" | "500" | "session-expired" | "submit-error" | "unknown";

export type ErrorVariantMap = Record<ErrorVariant, ErrorComponentProps>;

export const getCustomerErrorPresetMap = ({
  intl,
  closeErrorModal = () => {},
}: {
  intl: IntlShape;
  closeErrorModal?: () => void;
}): ErrorVariantMap => {
  const defaultPrimaryAction = {
    content: intl.formatMessage(ERROR_MESSAGES.wayflyerHome),
    onAction: () => window.location.replace(window.location.origin),
  };
  const defaultSecondaryAction = {
    content: intl.formatMessage({ id: "Go back", defaultMessage: "Go back" }),
    onAction: () => window.history.back(),
  };
  const contactSupport = () => {
    closeErrorModal();
    handleContactTeam();
  };
  const errorVariants: ErrorVariantMap = {
    "400": {
      title: intl.formatMessage({
        id: "error 400 default title",
        defaultMessage: "That link isn’t working",
      }),
      message: intl.formatMessage({
        id: "error 400 default message",
        defaultMessage:
          "It might be invalid or expired. You can double check the link to make sure you entered it correctly.",
      }),
      footerText: (
        <FormattedMessage
          id="error 400 default footer"
          defaultMessage="If you need help, <a>contact support</a>."
          values={{ a: (chunks) => <Link onClick={contactSupport}>{chunks}</Link> }}
        />
      ),
      primaryAction: defaultPrimaryAction,
    },
    "401": {
      title: intl.formatMessage({ id: "error 403 default title", defaultMessage: "Access denied" }),
      message: intl.formatMessage({
        id: "error 403 default message",
        defaultMessage: "You don’t have permission to access this page.",
      }),
      footerText: (
        <FormattedMessage
          id="error 403 default footer"
          defaultMessage="If you need access please <a>contact our support team</a>."
          values={{ a: (chunks) => <Link onClick={contactSupport}>{chunks}</Link> }}
        />
      ),
      primaryAction: defaultPrimaryAction,
      secondaryAction: defaultSecondaryAction,
    },
    "403": {
      title: intl.formatMessage({ id: "error 403 default title", defaultMessage: "Access denied" }),
      message: intl.formatMessage({
        id: "error 403 default message",
        defaultMessage: "You don’t have permission to access this page.",
      }),
      footerText: (
        <FormattedMessage
          id="error 403 default footer"
          defaultMessage="If you need access please <a>contact our support team</a>."
          values={{ a: (chunks) => <Link onClick={contactSupport}>{chunks}</Link> }}
        />
      ),
      primaryAction: defaultPrimaryAction,
      secondaryAction: defaultSecondaryAction,
    },
    "404": {
      title: intl.formatMessage({
        id: "error 404 default title",
        defaultMessage: "The page you’re looking for cannot be found",
      }),
      message: intl.formatMessage({
        id: "error 404 default message",
        defaultMessage:
          "Sorry we can’t find the page you’re looking for. This could be because the web address is incorrect or no longer exists.",
      }),
      footerText: (
        <FormattedMessage
          id="error 404 default footer"
          defaultMessage="For more information <a>contact our support team</a>."
          values={{ a: (chunks) => <Link onClick={contactSupport}>{chunks}</Link> }}
        />
      ),
      primaryAction: defaultPrimaryAction,
      secondaryAction: defaultSecondaryAction,
    },
    "500": {
      title: intl.formatMessage({
        id: "error 500 default title",
        defaultMessage: "Sorry, we’ve encountered an error",
      }),
      message: intl.formatMessage({
        id: "error 500 default message",
        defaultMessage:
          "We are currently experiencing an internal server error. We’re working hard to get this fixed. Please try again later.",
      }),
      footerText: (
        <FormattedMessage
          id="error 500 default footer"
          defaultMessage="For more information <a>contact our support team</a>."
          values={{ a: (chunks) => <Link onClick={contactSupport}>{chunks}</Link> }}
        />
      ),
      primaryAction: defaultPrimaryAction,
      secondaryAction: defaultSecondaryAction,
    },
    // Distinct from "401"/"403": the user's credentials have lapsed rather than being insufficient, so logging in
    // again fixes it. No "Go back" — the previous page cannot load either until they re-authenticate.
    "session-expired": {
      icon: <IconClock32Line />,
      title: intl.formatMessage({
        id: "error session expired title",
        defaultMessage: "Your session has expired",
      }),
      message: intl.formatMessage({
        id: "error session expired message",
        defaultMessage:
          "Your session timed out for security reasons. Please log in again to continue where you left off.",
      }),
      primaryAction: {
        content: intl.formatMessage({ id: "error session expired action", defaultMessage: "Log in" }),
        onAction: () => window.location.assign(getLoginUrl()),
      },
    },
    "submit-error": {
      title: intl.formatMessage({
        id: "error submit default title",
        defaultMessage: "Sorry, there was an error submitting your request",
      }),
      message: intl.formatMessage({
        id: "error unknown default message",
        defaultMessage: "We’re working hard to get this fixed. Please go back or try refreshing the page.",
      }),
      footerText: (
        <FormattedMessage
          id="error unknown default footer"
          defaultMessage="For more information <a>contact our support team</a>."
          values={{ a: (chunks) => <Link onClick={contactSupport}>{chunks}</Link> }}
        />
      ),
      primaryAction: defaultPrimaryAction,
      secondaryAction: defaultSecondaryAction,
    },
    unknown: {
      title: intl.formatMessage({
        id: "error unknown default title",
        defaultMessage: "Sorry, we’ve encountered an error",
      }),
      message: intl.formatMessage({
        id: "error unknown default message",
        defaultMessage: "We’re working hard to get this fixed. Please go back or try refreshing the page.",
      }),
      footerText: (
        <FormattedMessage
          id="error unknown default footer"
          defaultMessage="For more information <a>contact our support team</a>."
          values={{ a: (chunks) => <Link onClick={contactSupport}>{chunks}</Link> }}
        />
      ),
      primaryAction: defaultPrimaryAction,
      secondaryAction: defaultSecondaryAction,
    },
  };

  return errorVariants;
};

export const getStaffErrorPresetMap = (): ErrorVariantMap => {
  const defaultPrimaryAction = {
    content: "Wayflyer home",
    onAction: () => window.location.replace(window.location.origin),
  };
  const defaultSecondaryAction = {
    content: "Go back",
    onAction: () => window.history.back(),
  };
  const errorVariants: ErrorVariantMap = {
    "400": {
      title: "That link isn’t working",
      message:
        "It might be invalid or expired. You can double check the link to make sure you entered it correctly, or contact our support team.",
      primaryAction: defaultPrimaryAction,
    },
    "401": {
      title: "Access denied",
      message: "You don’t have permission to access this page. If you need access please contact our support team.",
      primaryAction: defaultPrimaryAction,
      secondaryAction: defaultSecondaryAction,
    },
    "403": {
      title: "Access denied",
      message: "You don’t have permission to access this page. If you need access please contact our support team.",
      primaryAction: defaultPrimaryAction,
      secondaryAction: defaultSecondaryAction,
    },
    "404": {
      title: "The page you’re looking for cannot be found",
      message:
        "Sorry we can’t find the page you’re looking for. This could be because the web address is incorrect or no longer exists. For more information contact our support team.",
      primaryAction: defaultPrimaryAction,
      secondaryAction: defaultSecondaryAction,
    },
    "500": {
      title: "Sorry, we’ve encountered an error",
      message:
        "We are currently experiencing an internal server error. We’re working hard to get this fixed. Please try again later. For more information contact our support team.",
      primaryAction: defaultPrimaryAction,
    },
    "session-expired": {
      title: "Your session has expired",
      message: "Log in again to continue.",
      primaryAction: {
        content: "Log in",
        onAction: () => window.location.assign(getLoginUrl()),
      },
    },
    "submit-error": {
      title: "Sorry, there was an error submitting your request",
      message:
        "We’re working hard to get this fixed. Please go back or try refreshing the page. For more information contact our support team.",
      primaryAction: defaultPrimaryAction,
      secondaryAction: defaultSecondaryAction,
    },
    unknown: {
      title: "Sorry, we’ve encountered an error",
      message:
        "We’re working hard to get this fixed. Please go back or try refreshing the page. For more information contact our support team",
      primaryAction: defaultPrimaryAction,
      secondaryAction: defaultSecondaryAction,
    },
  };

  return errorVariants;
};
